import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { Typography, Skeleton, DataTable, Spinner, Pagination } from '@chilecompra/react-kit/components'
import { PUBLIC_URL } from '../../config/settings/environment'
import { ANNUAL_PURCHASE_PLAN_TABS } from '../../config/settings/constant'
import { exportToExcel } from '../../modules/utils/exports'
import {
  onGetEncryptPurchaseOrderThunk,
  onGetOutsideAnnualPurchasePlanThunk
} from './OutsideAnnualPurchasePlanSection.actions'
import {
  ArrowDownIcon,
  Card,
  CardItem,
  Column,
  Link,
  NotFoundImg,
  PaginationColumn,
  Root,
  Row,
  Strong,
  TableContainer,
  TableLink,
  WarningImg
} from './OutsideAnnualPurchasePlanSection.styles'

/**
 * The Outside Annual Purchase Plan section container.
 */
const OutsideAnnualPurchasePlanSection = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const {
    size: { isTiny }
  } = useViewport()

  const { error, loading, hasData, year, publicationDate, gridData, excelData, loadingSpinner, page, records, total } =
    useSelector(state => state.outsideAnnualPurchasePlan)

  const lastUpdated = publicationDate ? ` el ${publicationDate}.` : '.'
  const isContentReady = !loading && !error
  const hasDataToShow = isContentReady && hasData
  const rowsPerPage = 10
  const [pageNumber, setPageNumber] = useState(1)
  useEffectOnce(() => {
    dispatch(onGetOutsideAnnualPurchasePlanThunk({ dni, page, rowsPerPage }))
  }, [dispatch, dni, page, rowsPerPage])

  const handleDownload = () => exportToExcel(excelData, 'Fuera del plan anual de compras')

  const handleKeyPressDownload = event => {
    if (event.charCode === 13 || event.charCode === 32) {
      event.preventDefault()
      handleDownload()
    }
  }

  const handlePurchaseOrders = id => () => {
    dispatch(onGetEncryptPurchaseOrderThunk({ id }))
  }

  const handleKeyPress = id => event => {
    if (event.charCode === 13 || event.charCode === 32) {
      event.preventDefault()
      dispatch(onGetEncryptPurchaseOrderThunk({ id }))
    }
  }

  const handlePagination = (_, selectPage) => {
    setPageNumber(selectPage)
    dispatch(onGetOutsideAnnualPurchasePlanThunk({ dni, page: selectPage, rowsPerPage }))
  }

  return (
    <Root>
      {!loading && (
        <>
          <Row>
            <Column maxWidth="595px" padding="10px 10px 0 0">
              <Typography tabIndex="0" color="black1" variant="h4" fontWeight="bold" fontSize="18px" lineHeight="22px">
                Órdenes de compra fuera del plan anual de compras
              </Typography>

              <Typography
                color="black3"
                display="inline"
                variant="subtitle"
                fontWeight="regular"
                fontSize="14px"
                lineHeight="18px"
                tabIndex="0"
              >
                <Strong>Fuente:</Strong> Plan anual de compras publicado por la institución en Mercado Público
                {lastUpdated}
              </Typography>
            </Column>
          </Row>
          {hasDataToShow && (
            <Row>
              <Column maxWidth="595px" padding="10px 0 24px 0">
                <Typography
                  color="black1"
                  display="inline"
                  variant="body2"
                  fontWeight="regular"
                  fontSize="14px"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Listado de las órdenes de compra generadas durante el
                  <Strong> año {year} </Strong>
                  <Strong>
                    que no fueron seleccionadas como parte del plan anual y no han sido enlazadas a ninguno de los ítems
                    que componen los proyectos de la planificación anual.
                  </Strong>
                </Typography>
              </Column>
            </Row>
          )}
        </>
      )}
      {hasDataToShow && !loadingSpinner && (
        <Card display="flex" color="blank" enableBorder padding="5px 16px 24px 16px">
          <TableContainer>
            <DataTable
              accessibility={{ label: 'Tabla de ordenes fuera del plan anual de compras' }}
              headers={[
                { label: 'ID', value: 'idPurchaseOrder' },
                { label: 'Monto', value: 'amount' },
                { label: 'Nombre', value: 'name' },
                { label: 'Proveedor', value: 'provider' },
                { label: 'Fecha envío OC', value: 'shippingDate' },
                { label: 'Estado OC', value: 'status' }
              ]}
              headersCellsProps={[
                { enableSort: false, width: '180px', enableFilter: true },
                { enableSort: false, width: '180px', enableFilter: true, alignment: 'left' },
                { enableSort: false, width: '500px', enableFilter: true, alignment: 'left' },
                { enableSort: false, width: '300px', enableFilter: true },
                { enableSort: false, width: '140px', enableFilter: true },
                { enableSort: false, width: '200px', enableFilter: true }
              ]}
              records={gridData}
              recordsCellsProps={[
                { alignment: 'left', verticalAlign: 'middle' },
                { alignment: 'left', verticalAlign: 'middle' },
                { alignment: 'left', verticalAlign: 'middle' },
                { alignment: 'left', verticalAlign: 'middle' },
                { alignment: 'center', verticalAlign: 'middle' },
                { alignment: 'left', verticalAlign: 'middle' }
              ]}
              recordsDecorators={[
                ({ idPurchaseOrder, id }) => (
                  <TableLink
                    tabIndex={0}
                    textDecoration="underline"
                    color="primary"
                    display="inline"
                    onClick={handlePurchaseOrders(id)}
                    onKeyPress={handleKeyPress(id)}
                  >
                    {idPurchaseOrder}
                  </TableLink>
                ),
                ({ amount }) => amount,
                ({ name }) => name,
                ({ provider }) => provider,
                ({ shippingDate }) => shippingDate,
                ({ status }) => status
              ]}
              initialRows={rowsPerPage}
              loading={loading}
            />
          </TableContainer>
          <PaginationColumn>
            <Pagination
              alignment="right"
              boundaryCount={1}
              siblingCount={1}
              count={total}
              disableResults={false}
              onChange={handlePagination}
              page={pageNumber}
              records={gridData?.length}
              size="small"
              total={records}
            />
          </PaginationColumn>

          <Link
            tabIndex={0}
            color="primary"
            textDecoration="underline"
            onClick={handleDownload}
            onKeyPress={handleKeyPressDownload}
          >
            <ArrowDownIcon /> Descargar listado en excel
          </Link>
        </Card>
      )}
      {isContentReady && !hasData && (
        <Card display="flex" color="blank" enableBorder margin="42px 0 32px 0">
          <CardItem direction="column">
            <NotFoundImg
              alt="No se encontraron resultados"
              margin="52px 0 0 0"
              src={`${PUBLIC_URL}/assets/images/notFound.svg`}
            />
            <Typography
              color="black1"
              variant="h4"
              fontWeight="bold"
              fontSize="18px"
              lineHeight="22px"
              margin="10px 0 0 0"
            >
              {ANNUAL_PURCHASE_PLAN_TABS.NO_INFO}
            </Typography>
            <Typography
              color="black3"
              variant="body2"
              fontWeight="regular"
              fontSize="14px"
              lineHeight="18px"
              margin="5px 0 52px 0"
              sx={{ maxWidth: '350px', textAlign: 'center' }}
            >
              {ANNUAL_PURCHASE_PLAN_TABS.ANNUAL_PURCHASE_PLAN_OUTSIDE.NO_DATA}
            </Typography>
          </CardItem>
        </Card>
      )}
      {error && (
        <Card
          display="flex"
          enableBorder
          color="blank"
          margin="43px 0 44px 0"
          alignItems="center"
          height={isTiny ? '139px' : '243px'}
        >
          <CardItem direction="row" padding="25px 16px">
            <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
            <Typography
              color="black1"
              variant="body1"
              fontWeight="regular"
              fontSize="14px"
              lineHeight="18px"
              margin="0 0 0 33px"
              tabIndex="0"
              sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
            >
              {ANNUAL_PURCHASE_PLAN_TABS.SERVICE_ERROR}
            </Typography>
          </CardItem>
        </Card>
      )}
      {!loading && loadingSpinner && (
        <Card margin="0 0 50px 0" display="flex" enableBorder color="blank">
          <CardItem direction="row" sx={{ margin: '97px auto' }}>
            <Spinner />
          </CardItem>
        </Card>
      )}
      {loading && !error && (
        <>
          <Row flexDirection="column" gap="10px" margin="0 0 24px 0">
            <Skeleton height="35px" variant="rectangular" width="55%" />
            <Skeleton height="15px" variant="rectangular" width="70%" />
            <Skeleton height="59px" variant="rectangular" width="70%" />
          </Row>
          <Skeleton height="674px" variant="rectangular" width="100%" />
        </>
      )}
    </Root>
  )
}

export default OutsideAnnualPurchasePlanSection
